// The Applinks Status Log format
// We use this to interchange between the various products
// Refer to `AppLinks.Creation.generateApplinksCreationRedirectionUrl` in
// legacy frontend for more details

import { APPLINKS_LIST_PAGE_URL_LEGACY } from './constants.tsx';

// Eventually we'll need to handle all these statuses:

export const STATUS_CREATION_SUCCESS = 'completed.creation';
export const STATUS_CREATION_FAILURE = 'failed.creation';
export const STATUS_CREATION_INPROGRESS = 'inprogress.creation';
export const STATUS_LOCAL_SUCCESS = 'completed.creation.local';
export const STATUS_RECIPROCAL_SUCCESS = 'completed.creation.reciprocal';
export const STATUS_RECIPROCAL_INPROGRESS = 'inprogress.creation.reciprocal';
export const STATUS_LOCAL_FAILURE = 'failed.creation.local';
export const STATUS_RECIPROCAL_FAILURE = 'failed.creation.reciprocal';
export const STATUS_REGISTERPROVIDER_2LOI_FAILURE = 'failed.registerprovider.2loi';

export type StatusLogEntry = {
	status: string;
	detail?: string;
};

export type StatusLog = StatusLogEntry[];

export const createStatusLog = (entries: StatusLogEntry[]): StatusLog => {
	return entries;
};

export const updateStatusLog = (statusLog: StatusLog, newEntry: StatusLogEntry): StatusLog => {
	return [...statusLog, newEntry];
};

/**
 * Returns the URL for the applinks administration page.
 * This is used to redirect users to the applinks administration page
 *
 * @param remoteBaseUrl The base URL of the application
 * @param typeId The type ID of the application (i.e. 'jira', 'confluence')
 * @return The URL for the applinks administration page
 */
export const getApplinksAdminUrl = (remoteBaseUrl: string, typeId: string): string => {
	const suffix =
		typeId === 'confluence' ? '/admin/listapplicationlinks.action' : APPLINKS_LIST_PAGE_URL_LEGACY;

	return remoteBaseUrl + suffix;
};

export type CreationState = {
	applinkCreationStatusLog: StatusLog;
	applinkOriginalCreatedId: string;
	sharedUserbase: boolean;
	applinkConfigInUserAuth: boolean;
	applinkConfigInServerAuth: boolean;
	applinkConfigInPreAuth: boolean;
	applinkConfigOutUserAuth: boolean;
	applinkConfigOutServerAuth: boolean;
	applinkConfigOutPreAuth: boolean;
	applinkStartingUrl?: string;
	applinkStartingType?: string;
	creatingReciprocal?: boolean;
};

/**
 * The creation state object to be passed to external products. You shouldn't need to change this as most
 * of the parameters and flows are now deprecated. We only really support one type of OAuth 1.0 flow now,
 * which is 3LO+2LO. 2LOi is not supported anymore.
 *
 * @param startingUrl The URL of this instance
 * @param startingTypeId The type ID of this instance (i.e. 'jira')
 * @param originalCreatedId The ID of the applink created on the local side
 * @returns CreationState object to pass to external products
 */
export const getDefaultCreationState = (
	startingUrl: string,
	startingTypeId: string,
	originalCreatedId: string,
): CreationState => {
	return {
		applinkCreationStatusLog: createStatusLog([
			{ status: STATUS_CREATION_INPROGRESS },
			{ status: STATUS_LOCAL_SUCCESS },
			{ status: STATUS_RECIPROCAL_INPROGRESS },
		]),
		applinkOriginalCreatedId: originalCreatedId,
		sharedUserbase: false,
		applinkConfigInUserAuth: true, // 3LO
		applinkConfigInServerAuth: true, // 2LO
		applinkConfigInPreAuth: true, // 2LOi
		applinkConfigOutUserAuth: true,
		applinkConfigOutServerAuth: true,
		applinkConfigOutPreAuth: true,
		applinkStartingUrl: startingUrl,
		applinkStartingType: startingTypeId,
		creatingReciprocal: true,
	};
};

const generateUrl = (remoteUrl: string, parametersMap?: Record<string, string>): string => {
	if (!parametersMap || Object.keys(parametersMap).length === 0) {
		return remoteUrl;
	}

	const params = Object.entries(parametersMap)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('&');

	return remoteUrl.includes('?') ? `${remoteUrl}&${params}` : `${remoteUrl}?${params}`;
};

/**
 * Create a redirection URL for the applinks creation flow, using the
 * data provided in the creation state.
 *
 * This handles URL encoding in the specific way that the legacy frontend expects.
 * @param remoteBaseUrl The base URL of the remote application
 * @param remoteTypeId The type ID of the remote application (i.e. 'jira
 * @param creationState The creation state object containing the parameters to be passed
 * @returns The redirection URL for the applinks creation flow
 */
export const generateApplinksCreationRedirectionUrl = (
	remoteBaseUrl: string,
	remoteTypeId: string,
	creationState: CreationState,
): string => {
	// In the original applinks frontend, the status log was stored as a string. This means in the final URL,
	// the status log is double stringified. This is a hack to maintain compatibility with the legacy frontend.
	const parameterMap: Record<string, string> = Object.fromEntries(
		Object.entries(creationState).map(([key, value]) => {
			if (key === 'applinkCreationStatusLog') {
				// Yes, I know it's awful
				return [key, JSON.stringify(JSON.stringify(creationState.applinkCreationStatusLog))];
			}
			return [key, JSON.stringify(value)];
		}),
	);

	return generateUrl(getApplinksAdminUrl(remoteBaseUrl, remoteTypeId), parameterMap);
};

const getAndDecode = (url: URL, param: string): string | undefined => {
	const value = url.searchParams.get(param);
	if (value !== null) {
		const decodedFieldValue = decodeURIComponent(value);
		try {
			// @ts-expect-error - TS2322 Typescript 5.9.2 upgrade
			return value ? JSON.parse(decodedFieldValue) : null;
		} catch {
			// If parsing fails, we return the decoded value as-is
			return decodedFieldValue;
		}
	}
	return undefined;
};

/**
 * This function parses the creation state from the URL. It looks for specific query
 * parameters that are used to store the creation state. If the parameters are found,
 * it returns a CreationState object. If not, it returns undefined.
 *
 * @param url The URL to parse the creation state from
 * @returns CreationState object if found, otherwise undefined
 */
export const parseCreationStateFromUrl = (url: URL): CreationState | null => {
	const applinkCreationStatusLog = getAndDecode(url, 'applinkCreationStatusLog');
	if (!applinkCreationStatusLog) {
		return null; // No creation state found
	}

	const applinkStartingUrl = getAndDecode(url, 'applinkStartingUrl');
	const applinkStartingType = getAndDecode(url, 'applinkStartingType');
	const applinkOriginalCreatedId = getAndDecode(url, 'applinkOriginalCreatedId');
	const sharedUserbase = getAndDecode(url, 'sharedUserbase') === 'true';
	const applinkConfigInUserAuth = getAndDecode(url, 'applinkConfigInUserAuth') === 'true';
	const applinkConfigInServerAuth = getAndDecode(url, 'applinkConfigInServerAuth') === 'true';
	const applinkConfigInPreAuth = getAndDecode(url, 'applinkConfigInPreAuth') === 'true';
	const applinkConfigOutUserAuth = getAndDecode(url, 'applinkConfigOutUserAuth') === 'true';
	const applinkConfigOutServerAuth = getAndDecode(url, 'applinkConfigOutServerAuth') === 'true';
	const applinkConfigOutPreAuth = getAndDecode(url, 'applinkConfigOutPreAuth') === 'true';
	const creatingReciprocal = getAndDecode(url, 'creatingReciprocal') === 'true';

	const statusLog = JSON.parse(applinkCreationStatusLog);

	return {
		applinkCreationStatusLog: statusLog,
		applinkOriginalCreatedId: applinkOriginalCreatedId || '',
		sharedUserbase,
		applinkConfigInUserAuth,
		applinkConfigInServerAuth,
		applinkConfigInPreAuth,
		applinkConfigOutUserAuth,
		applinkConfigOutServerAuth,
		applinkConfigOutPreAuth,
		applinkStartingUrl: applinkStartingUrl || '',
		applinkStartingType: applinkStartingType || '',
		creatingReciprocal,
	};
};
